import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { ApiError } from '~/api/errors';
import { queryKeys } from '~/api/queryKeys';
import { extractApiError } from '~/utils/extract-api-error';

import { stopExperiment, type ExperimentStopPayload } from '../services/experiment-stop.service';

interface StopExperimentArgs {
  expId: string;
  payload: ExperimentStopPayload;
}

/**
 * Mutation that halts a running experiment (`POST /experiments/{id}/stop`).
 * Invalidates the experiment detail + list caches on success so the new
 * terminal status renders immediately. 409 (already terminal) gets a
 * specific "already stopped" toast rather than the generic failure.
 */
export function useStopExperiment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ expId, payload }: StopExperimentArgs) => stopExperiment(expId, payload),
    onSuccess: (_data, { expId }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.experiments.detail(expId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.experiments.all });
    },
    onError: (err) => {
      if (err instanceof ApiError && err.statusCode === 409) {
        toast.error(
          extractApiError(
            err,
            'This experiment is already in a terminal state — refresh to see the latest status.',
          ),
        );
        return;
      }
      toast.error(extractApiError(err, 'Could not stop the experiment. Please try again.'));
    },
  });
}
